<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import TasksAPI from 'dashboard/api/tasks';
import { TASK_CREATOR_USER_IDS } from 'dashboard/constants/taskPermissions';

const props = defineProps({
  tasks: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});
const emit = defineEmits(['changed', 'open-new']);

const store = useStore();
const router = useRouter();
const canAssignTasks = computed(() =>
  TASK_CREATOR_USER_IDS.includes(store.getters.getCurrentUser?.id)
);

// === Filtro · vista por defecto: Abiertas ===
const filter = ref('abiertas'); // abiertas | hoy | vencidas | todas
const searchQuery = ref('');
const selectedId = ref(null);
const busy = ref(false);

const now = () => Date.now() / 1000;
const todayStart = () => { const d = new Date(); d.setHours(0,0,0,0); return d.getTime() / 1000; };
const tomorrowStart = () => { const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() + 1); return d.getTime() / 1000; };
const weekEnd = () => { const d = new Date(); d.setHours(23,59,59,999); d.setDate(d.getDate() + 7); return d.getTime() / 1000; };

const isOpen = t => t.status !== 'hecha';
const isOverdue = t => isOpen(t) && t.due_date && t.due_date < now();
const isToday = t => isOpen(t) && t.due_date && t.due_date >= todayStart() && t.due_date < tomorrowStart();
const isTomorrow = t => isOpen(t) && t.due_date && t.due_date >= tomorrowStart() && t.due_date < tomorrowStart() + 86400;
const isThisWeek = t => isOpen(t) && t.due_date && t.due_date >= tomorrowStart() + 86400 && t.due_date < weekEnd();
const isLater = t => isOpen(t) && t.due_date && t.due_date >= weekEnd();
const isNoDate = t => isOpen(t) && !t.due_date;
const isDoneToday = t => t.status === 'hecha' && t.completed_at && t.completed_at >= todayStart();

// === Filtrar por filter activo + search ===
const matchesSearch = t => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return true;
  return `${t.title} ${t.description || ''} ${t.contact?.name || ''} ${t.branch?.name || ''}`.toLowerCase().includes(q);
};

const visible = computed(() => {
  let list = props.tasks.filter(matchesSearch);
  if (filter.value === 'abiertas') list = list.filter(isOpen);
  if (filter.value === 'hoy') list = list.filter(t => isToday(t) || isOverdue(t));
  if (filter.value === 'vencidas') list = list.filter(isOverdue);
  // 'todas' = todo lo que viene
  return list;
});

// === Métricas top ===
const overdueCount = computed(() => props.tasks.filter(isOverdue).length);
const todayCount = computed(() => props.tasks.filter(isToday).length);
const weekCount = computed(() => props.tasks.filter(t => isOpen(t) && t.due_date && t.due_date < weekEnd()).length);
const doneTodayCount = computed(() => props.tasks.filter(isDoneToday).length);

// === Secciones del timeline ===
const sections = computed(() => {
  const list = visible.value;
  const byPrio = (a, b) => {
    if (a.priority !== b.priority) return a.priority === 'alta' ? -1 : 1;
    return (a.due_date || Infinity) - (b.due_date || Infinity);
  };
  const buckets = [
    { key: 'overdue', label: 'VENCIDAS', tone: 'rose', tasks: list.filter(isOverdue).sort(byPrio) },
    { key: 'today', label: 'HOY', tone: 'gold', tasks: list.filter(isToday).sort(byPrio) },
    { key: 'tomorrow', label: 'MAÑANA', tone: 'ink-2', tasks: list.filter(isTomorrow).sort(byPrio) },
    { key: 'week', label: 'ESTA SEMANA', tone: 'ink-3', tasks: list.filter(isThisWeek).sort(byPrio) },
    { key: 'later', label: 'MÁS ADELANTE', tone: 'ink-3', tasks: list.filter(isLater).sort(byPrio) },
    { key: 'nodate', label: 'SIN FECHA', tone: 'ink-4', tasks: list.filter(isNoDate).sort(byPrio) },
  ];
  // Solo mostrar Hechas hoy si el filtro 'todas' está activo
  if (filter.value === 'todas') {
    buckets.push({ key: 'done', label: 'HECHAS HOY', tone: 'ink-4', tasks: list.filter(isDoneToday) });
  }
  return buckets.filter(b => b.tasks.length > 0);
});

// === Helpers de formato ===
const formatDue = ts => {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dDay = new Date(d); dDay.setHours(0,0,0,0);
  const time = d.toLocaleTimeString('es-DO', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dDay.getTime() < today.getTime()) {
    const days = Math.floor((today.getTime() - dDay.getTime()) / 86400000);
    return `Hace ${days} día${days===1?'':'s'}`;
  }
  if (dDay.getTime() === today.getTime()) return time;
  if (dDay.getTime() === tomorrow.getTime()) return `Mañana ${time}`;
  return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
};
const formatDueSub = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
};
const channelLabel = task => {
  // El JSON view actual no incluye channel_type. Si el conversation está embebido lo intenta sacar.
  const ch = task.conversation?.channel_type || task.inbox?.channel_type || '';
  const map = {
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::Api': 'Bridge',
    'Channel::FacebookPage': 'Facebook',
    'Channel::Instagram': 'Instagram',
  };
  return map[ch] || ch.replace('Channel::', '') || '';
};
const initials = name => (name||'??').trim().split(/\s+/).slice(0,2).map(s=>s[0]?.toUpperCase()||'').join('') || '??';

// === Selección + detalle ===
const selected = computed(() => props.tasks.find(t => t.id === selectedId.value) || null);
function selectTask(t) { selectedId.value = t.id; }

// === Acciones ===
async function markDone(t) {
  if (busy.value) return;
  busy.value = true;
  try {
    await TasksAPI.markStatus(t.id, 'hecha');
    useAlert('✓ Tarea marcada como hecha');
    emit('changed');
  } catch (_) { useAlert('No se pudo actualizar.'); }
  finally { busy.value = false; }
}
async function markInProgress(t) {
  if (busy.value) return;
  busy.value = true;
  try { await TasksAPI.markStatus(t.id, 'en_proceso'); emit('changed'); }
  catch (_) {} finally { busy.value = false; }
}
async function snoozeTomorrow(t) {
  if (busy.value) return;
  busy.value = true;
  try {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
    await TasksAPI.update(t.id, { due_date: d.toISOString() });
    useAlert('Pospuesta a mañana 9 AM');
    emit('changed');
  } catch (_) { useAlert('No se pudo posponer.'); }
  finally { busy.value = false; }
}
function openChat(t) {
  if (!t?.conversation_display_id) return;
  const accountId = store.getters.getCurrentAccountId;
  router.push({ path: `/app/accounts/${accountId}/conversations/${t.conversation_display_id}` });
}
const isOverdueT = t => t && t.status !== 'hecha' && t.due_date && t.due_date < now();
const isAltaT = t => t?.priority === 'alta';
</script>

<template>
  <div class="mtt-root">
    <!-- ============ HEADER · métricas + toolbar ============ -->
    <header class="mtt-header">
      <div class="mtt-metrics">
        <div class="mtt-metric mtt-metric--rose">
          <div class="ic">🔥</div>
          <div class="val">{{ overdueCount }}</div>
          <div class="lbl">VENCIDAS</div>
        </div>
        <div class="mtt-metric">
          <div class="ic">📅</div>
          <div class="val" :class="todayCount > 0 ? 'val-gold' : ''">{{ todayCount }}</div>
          <div class="lbl">HOY</div>
        </div>
        <div class="mtt-metric">
          <div class="ic">🗓️</div>
          <div class="val">{{ weekCount }}</div>
          <div class="lbl">SEMANA</div>
        </div>
        <div class="mtt-metric mtt-metric--gold">
          <div class="ic">✓</div>
          <div class="val">{{ doneTodayCount }}</div>
          <div class="lbl">HECHAS</div>
        </div>
      </div>

      <div class="mtt-toolbar">
        <div class="mtt-search-wrap">
          <span class="mtt-search-ic">⌕</span>
          <input v-model="searchQuery" class="mtt-search" placeholder="Buscar tarea…" />
        </div>
        <button v-if="canAssignTasks" class="mtt-new" @click="emit('open-new')">
          <span class="mtt-new-plus">+</span>
          <span class="mtt-new-lbl">Nueva</span>
        </button>
      </div>

      <div class="mtt-filters">
        <button
          v-for="f in [
            { k: 'abiertas', l: 'Abiertas' },
            { k: 'hoy', l: 'Hoy' },
            { k: 'vencidas', l: 'Vencidas' },
            { k: 'todas', l: 'Todas' },
          ]"
          :key="f.k"
          class="mtt-filter"
          :class="filter === f.k ? 'mtt-filter--active' : ''"
          @click="filter = f.k"
        >{{ f.l }}</button>
      </div>
    </header>

    <!-- ============ BODY · timeline + detalle ============ -->
    <div class="mtt-body">
      <!-- TIMELINE -->
      <div class="mtt-timeline">
        <div v-if="loading && !tasks.length" class="mtt-empty">Cargando…</div>
        <div v-else-if="!sections.length" class="mtt-empty">
          <div class="mtt-empty-icon">✓</div>
          <div class="mtt-empty-title">No hay tareas {{ filter === 'todas' ? '' : filter }}</div>
          <div class="mtt-empty-sub">Cuando te asignen una nueva aparecerá aquí</div>
        </div>

        <template v-else>
          <section v-for="sec in sections" :key="sec.key" class="mtt-section">
            <div class="mtt-section-head">
              <span class="mtt-section-label" :class="`mtt-tone-${sec.tone}`">{{ sec.label }}</span>
              <div class="mtt-section-rule" />
              <span class="mtt-section-count">{{ sec.tasks.length }}</span>
            </div>

            <button
              v-for="task in sec.tasks"
              :key="task.id"
              type="button"
              class="mtt-row"
              :class="[
                selectedId === task.id ? 'mtt-row--selected' : '',
                isOverdueT(task) ? 'mtt-row--overdue' : '',
                isAltaT(task) && !isOverdueT(task) ? 'mtt-row--alta' : '',
                task.status === 'hecha' ? 'mtt-row--done' : '',
              ]"
              @click="selectTask(task)"
            >
              <span class="mtt-av">{{ initials(task.contact?.name || task.branch?.name) }}</span>
              <div class="mtt-row-main">
                <div class="mtt-row-title">{{ task.title }}</div>
                <div v-if="task.branch?.name || task.contact?.name" class="mtt-row-sub">
                  <span v-if="task.branch?.name" class="mtt-bchip">{{ task.branch.name }}</span>
                  <span v-if="task.contact?.name" class="mtt-cname">{{ task.contact.name }}</span>
                </div>
                <div class="mtt-row-foot">
                  <span v-if="isAltaT(task) && task.status !== 'hecha'" class="mtt-prio">ALTA</span>
                  <span class="mtt-due-badge" :class="isOverdueT(task) ? 'is-overdue' : ''">
                    {{ formatDue(task.due_date) }}<template v-if="task.due_date && sec.key !== 'today' && formatDueSub(task.due_date)"> · {{ formatDueSub(task.due_date) }}</template>
                  </span>
                </div>
              </div>
            </button>
          </section>
        </template>
      </div>

    </div>

    <!-- DETALLE · modal centrado (Propuesta 1 · Victor 2026-06-11): reemplaza el
         panel lateral que se cortaba en pantallas chicas. Centrado y responsive. -->
    <div v-if="selected" class="mtt-modal-scrim" @click.self="selectedId = null">
      <div class="mtt-modal">
        <div class="mtt-modal-top">
          <span v-if="isAltaT(selected)" class="mtt-mchip mtt-mchip--alta">⚡ ALTA</span>
          <span v-else class="mtt-mchip mtt-mchip--normal">NORMAL</span>
          <span v-if="isOverdueT(selected)" class="mtt-mchip mtt-mchip--due">⏰ VENCIDA · {{ formatDue(selected.due_date) }}</span>
          <span v-else-if="selected.due_date" class="mtt-mchip" :class="isToday(selected) ? 'mtt-mchip--today' : 'mtt-mchip--ok'">⏰ {{ formatDue(selected.due_date) }}</span>
          <span v-else class="mtt-mchip mtt-mchip--ok">SIN FECHA</span>
          <button class="mtt-modal-x" title="Cerrar" @click="selectedId = null">✕</button>
        </div>

        <div class="mtt-modal-body">
          <h2 class="mtt-detail-title">{{ selected.title }}</h2>

          <div class="mtt-detail-grid">
            <div v-if="selected.contact?.name">
              <div class="mtt-detail-key">CLIENTE</div>
              <div class="mtt-detail-val">
                <span class="mtt-avatar">{{ initials(selected.contact.name) }}</span>
                <span>{{ selected.contact.name }}</span>
              </div>
            </div>
            <div v-if="selected.branch?.name">
              <div class="mtt-detail-key">SUCURSAL</div>
              <div class="mtt-detail-val">📍 {{ selected.branch.name }}</div>
            </div>
            <div v-if="selected.created_by?.name">
              <div class="mtt-detail-key">ASIGNADA POR</div>
              <div class="mtt-detail-val"><span class="mtt-avatar">{{ initials(selected.created_by.name) }}</span>{{ selected.created_by.name }}</div>
            </div>
            <div v-if="selected.assignee?.name">
              <div class="mtt-detail-key">ASIGNADA A</div>
              <div class="mtt-detail-val"><span class="mtt-avatar">{{ initials(selected.assignee.name) }}</span>{{ selected.assignee.name }}</div>
            </div>
            <div>
              <div class="mtt-detail-key">CREADA</div>
              <div class="mtt-detail-val">{{ new Date(selected.created_at * 1000).toLocaleString('es-DO', { day:'numeric', month:'short', hour:'numeric', minute:'2-digit', hour12: true }) }}</div>
            </div>
            <div v-if="selected.status === 'hecha' && selected.completed_at">
              <div class="mtt-detail-key">COMPLETADA</div>
              <div class="mtt-detail-val">{{ new Date(selected.completed_at * 1000).toLocaleString('es-DO', { day:'numeric', month:'short', hour:'numeric', minute:'2-digit', hour12: true }) }}</div>
            </div>
          </div>

          <div v-if="selected.description" class="mtt-detail-notes">
            <div class="mtt-detail-key">NOTAS</div>
            <p>{{ selected.description }}</p>
          </div>
        </div>

        <div class="mtt-modal-actions">
          <button v-if="selected.status !== 'hecha'" class="mtt-mbtn mtt-mbtn--gold" @click="markDone(selected)">✓ Marcar hecha</button>
          <div v-if="selected.status !== 'hecha'" class="mtt-modal-actions-row">
            <button v-if="selected.status === 'pendiente'" class="mtt-mbtn" @click="markInProgress(selected)">▶ En proceso</button>
            <button class="mtt-mbtn" @click="snoozeTomorrow(selected)">⏰ Posponer</button>
          </div>
          <div class="mtt-modal-actions-row">
            <button v-if="selected.conversation_display_id" class="mtt-mbtn mtt-mbtn--ghost" @click="openChat(selected)">💬 Abrir chat</button>
            <button class="mtt-mbtn mtt-mbtn--ghost" @click="selectedId = null">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:root, .mtt-root {
  --bg-0: #0b0c0e;
  --bg-1: #111317;
  --bg-2: #16181d;
  --bg-hover: #1c1f25;
  --border: rgba(255,255,255,.05);
  --border-strong: rgba(255,255,255,.08);
  --ink-1: #e8e9ed;
  --ink-2: #a8aab1;
  --ink-3: #6e7079;
  --ink-4: #494b53;
  --gold: #c9a35a;
  --gold-soft: rgba(201,163,90,.10);
  --gold-line: rgba(201,163,90,.24);
  --rose: #d4a8a8;
  --rose-soft: rgba(212,168,168,.10);
  --rose-line: rgba(212,168,168,.22);
}
.mtt-root {
  display: flex; flex-direction: column;
  height: 100%; min-height: 0;
  background: var(--bg-0);
  color: var(--ink-1);
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  font-feature-settings: 'tnum';
  letter-spacing: -0.005em;
}

/* ============ Header ============ */
.mtt-header { flex-shrink: 0; border-bottom: 1px solid var(--border); }

/* ===== Métricas con ícono (tarjetas) ===== */
.mtt-metrics {
  display: flex; gap: 8px;
  padding: 14px 14px 8px;
}
.mtt-metric {
  flex: 1; min-width: 0;
  padding: 11px 11px 10px;
  border-radius: 14px;
  background: linear-gradient(155deg, rgba(255,255,255,.04), rgba(255,255,255,.01));
  border: 1px solid var(--border);
}
.mtt-metric .ic { font-size: 13px; line-height: 1; margin-bottom: 8px; opacity: .9; }
.mtt-metric .val {
  font-size: 22px; font-weight: 300; color: var(--ink-1);
  line-height: 1; font-variant-numeric: tabular-nums;
}
.mtt-metric .lbl {
  font-size: 8px; letter-spacing: 0.1em; color: var(--ink-3);
  font-weight: 600; margin-top: 5px;
}
.mtt-metric--rose { border-color: var(--rose-line); }
.mtt-metric--rose .val { color: var(--rose); }
.mtt-metric--gold { border-color: var(--gold-line); }
.mtt-metric--gold .val { color: var(--gold); }
.val-gold { color: var(--gold); }
.val-rose { color: var(--rose); }

/* ===== Toolbar unificada (buscador + Nueva) ===== */
.mtt-toolbar {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 14px 10px;
}
.mtt-search-wrap {
  flex: 1; min-width: 0;
  display: flex; align-items: center; gap: 8px;
  background: var(--bg-2);
  border: 1px solid var(--border);
  border-radius: 11px;
  padding: 0 12px;
  transition: border-color .15s;
}
.mtt-search-wrap:focus-within { border-color: rgba(201,163,90,.4); }
.mtt-search-ic { color: var(--ink-4); font-size: 14px; flex-shrink: 0; }
.mtt-search {
  flex: 1; min-width: 0;
  padding: 9px 0; border: none; background: transparent;
  font-size: 12px; color: var(--ink-1);
}
.mtt-search:focus { outline: none; }
.mtt-search::placeholder { color: var(--ink-4); }
.mtt-new {
  flex-shrink: 0;
  height: 38px; padding: 0 16px;
  border-radius: 11px; border: none; cursor: pointer;
  background: linear-gradient(135deg, #e0c184, #c9a35a);
  color: #1a1610; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; gap: 6px;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(201,163,90,.22), inset 0 1px 0 rgba(255,255,255,.35);
  transition: filter .15s;
}
.mtt-new:hover { filter: brightness(1.06); }
.mtt-new-plus { font-size: 15px; font-weight: 600; line-height: 1; }

/* ===== Filtros (pills) ===== */
.mtt-filters {
  display: flex; gap: 6px; flex-wrap: wrap;
  padding: 0 14px 12px;
}
.mtt-filter {
  padding: 5px 13px; border-radius: 20px;
  font-size: 11px; color: var(--ink-3);
  background: transparent; border: 1px solid var(--border);
  cursor: pointer; transition: all .15s;
}
.mtt-filter:hover { color: var(--ink-2); border-color: var(--border-strong); }
.mtt-filter--active {
  color: var(--gold);
  background: var(--gold-soft);
  border-color: var(--gold-line);
}

/* ============ Body ============ */
/* Lista a ancho completo (Victor 2026-06-11): el detalle ya no es split lateral
   sino modal centrado, así que el timeline ocupa todo el ancho disponible. */
.mtt-body {
  flex: 1; display: flex; min-height: 0;
}
.mtt-timeline {
  flex: 1; width: auto; min-width: 0;
  overflow-y: auto;
  background: var(--bg-1);
}

/* ============ Detalle · modal centrado (Propuesta 1) ============ */
.mtt-modal-scrim {
  position: fixed; inset: 0; z-index: 9100;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  background: rgba(4,4,6,.66);
  backdrop-filter: blur(5px);
}
.mtt-modal {
  position: relative; width: 100%; max-width: 460px;
  max-height: calc(100vh - 40px); overflow-y: auto;
  background: linear-gradient(180deg, #16161a 0%, #0d0d10 100%);
  border: 1px solid var(--border-strong);
  border-radius: 20px;
  box-shadow: 0 40px 90px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.05);
}
.mtt-modal::before {
  content: ""; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(201,163,90,.6), transparent);
}
.mtt-modal-top {
  display: flex; align-items: center; gap: 8px;
  padding: 16px 16px 0; flex-wrap: wrap;
}
.mtt-mchip {
  font-size: 10.5px; font-weight: 700; letter-spacing: .04em;
  padding: 4px 10px; border-radius: 7px; white-space: nowrap;
}
.mtt-mchip--alta { color: var(--gold); background: var(--gold-soft); }
.mtt-mchip--normal { color: var(--ink-3); border: 1px solid var(--border-strong); }
.mtt-mchip--due { color: var(--rose); background: var(--rose-soft); }
.mtt-mchip--today { color: var(--gold); background: var(--gold-soft); }
.mtt-mchip--ok { color: var(--ink-3); background: rgba(255,255,255,.04); }
.mtt-modal-x {
  margin-left: auto; width: 30px; height: 30px; border-radius: 9px;
  border: 1px solid var(--border-strong); background: rgba(255,255,255,.03);
  color: var(--ink-3); font-size: 14px; cursor: pointer;
  display: grid; place-content: center;
}
.mtt-modal-x:hover { color: var(--ink-1); }
.mtt-modal-body { padding: 16px 22px 4px; }
.mtt-modal .mtt-detail-title { font-size: 20px; }
.mtt-modal .mtt-detail-grid { gap: 18px 22px; margin-top: 20px; }
.mtt-modal-actions {
  padding: 16px; display: flex; flex-direction: column; gap: 9px;
  border-top: 1px solid var(--border); margin-top: 10px;
}
.mtt-modal-actions-row { display: flex; gap: 9px; }
.mtt-mbtn {
  flex: 1; height: 42px; border-radius: 12px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  border: 1px solid var(--border-strong); background: var(--bg-2);
  color: var(--ink-1); transition: .15s;
}
.mtt-mbtn:hover { background: var(--bg-hover); }
.mtt-mbtn--gold {
  background: var(--gold); color: #1a1610; border: none; font-weight: 700;
  box-shadow: 0 8px 22px rgba(201,163,90,.25);
}
.mtt-mbtn--gold:hover { background: #d4b87a; }
.mtt-mbtn--ghost { background: transparent; }
@media (max-width: 600px) {
  .mtt-modal-scrim { padding: 12px; align-items: flex-end; }
  .mtt-modal { max-height: 92vh; border-radius: 20px 20px 16px 16px; }
  .mtt-modal-body { padding: 14px 16px 4px; }
}

/* ============ Timeline secciones ============ */
.mtt-section { padding: 10px 0 2px; }
.mtt-section-head {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 16px 10px;
}
.mtt-section-label {
  font-size: 10px; letter-spacing: 0.2em; font-weight: 600;
}
.mtt-section-rule {
  flex: 1; height: 1px; background: var(--border);
}
.mtt-section-count {
  font-size: 10px; color: var(--ink-4);
  font-variant-numeric: tabular-nums;
}
.mtt-tone-gold { color: var(--gold); }
.mtt-tone-rose { color: var(--rose); }
.mtt-tone-ink-2 { color: var(--ink-2); }
.mtt-tone-ink-3 { color: var(--ink-3); }
.mtt-tone-ink-4 { color: var(--ink-4); }
.mtt-sep { color: var(--ink-4); }

/* ============ Tarjetas (Tarjetas Premium) ============ */
.mtt-row {
  display: flex; align-items: flex-start; gap: 12px;
  width: auto; margin: 0 14px 10px; padding: 14px;
  text-align: left; cursor: pointer;
  background: var(--bg-1);
  border: 1px solid var(--border);
  border-radius: 16px;
  transition: transform .14s, background .14s, border-color .14s;
}
.mtt-row:hover {
  background: var(--bg-2);
  border-color: var(--border-strong);
  transform: translateY(-1px);
}
.mtt-row--selected { background: rgba(201,163,90,.05); border-color: var(--gold-line); }
.mtt-row--overdue { border-left: 3px solid var(--rose); }
.mtt-row--alta { border-left: 3px solid var(--gold); }
.mtt-row--done { opacity: .55; }
.mtt-row--done .mtt-row-title { color: var(--ink-3); text-decoration: line-through; }

.mtt-av {
  width: 40px; height: 40px; flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(140deg, #2a2d33, #16181d);
  border: 1px solid var(--border-strong);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; color: var(--ink-2); font-weight: 600;
}
.mtt-row--overdue .mtt-av { color: var(--rose); border-color: var(--rose-line); }

.mtt-row-main { flex: 1; min-width: 0; }
.mtt-row-title {
  font-size: 14px; font-weight: 500; color: var(--ink-1);
  line-height: 1.35;
}
.mtt-row-sub {
  margin-top: 6px;
  display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
}
.mtt-bchip {
  font-size: 10px; padding: 3px 8px; border-radius: 7px;
  background: var(--bg-hover); color: var(--ink-2);
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.mtt-cname { font-size: 11px; color: var(--ink-3); }
.mtt-row-foot {
  display: flex; align-items: center; gap: 8px;
  margin-top: 10px;
}
.mtt-prio {
  font-size: 9px; letter-spacing: 0.08em; font-weight: 700;
  color: var(--gold); background: var(--gold-soft);
  padding: 3px 8px; border-radius: 6px;
}
.mtt-due-badge {
  font-size: 10px; color: var(--ink-3); font-weight: 600;
  background: rgba(255,255,255,.04);
  padding: 3px 9px; border-radius: 6px;
  margin-left: auto;
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
.mtt-due-badge.is-overdue { color: var(--rose); background: var(--rose-soft); }

/* ============ Empty ============ */
.mtt-empty {
  padding: 60px 30px; text-align: center;
  color: var(--ink-3);
}
.mtt-empty-icon {
  font-size: 36px; color: var(--ink-4);
  margin-bottom: 12px; font-weight: 300;
}
.mtt-empty-title { font-size: 14px; color: var(--ink-2); }
.mtt-empty-sub { font-size: 11px; color: var(--ink-4); margin-top: 6px; }

/* ============ Detalle (contenido · reutilizado por el modal) ============ */
.mtt-detail-title {
  font-size: 22px; font-weight: 300; line-height: 1.25;
  color: var(--ink-1); letter-spacing: -0.015em;
}
.mtt-detail-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 22px 36px;
  margin-top: 28px;
}
.mtt-detail-key {
  font-size: 9px; letter-spacing: 0.18em; font-weight: 600;
  color: var(--ink-4);
  margin-bottom: 4px;
}
.mtt-detail-val {
  font-size: 13px; color: var(--ink-1);
  display: flex; align-items: center; gap: 8px;
}
.mtt-avatar {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #2a2d34 0%, #1a1d22 100%);
  color: var(--ink-2);
  font-size: 9px; font-weight: 600;
  display: inline-flex; align-items: center; justify-content: center;
  letter-spacing: 0;
}

.mtt-detail-notes {
  margin-top: 28px; padding-top: 24px;
  border-top: 1px solid var(--border);
}
.mtt-detail-notes p {
  font-size: 13px; color: var(--ink-2);
  line-height: 1.6; margin-top: 6px;
  max-width: 640px;
}
</style>
